// Reproductor de video inline sin controles. Se monta mientras un sample
// está sonando y se desmonta al terminar (el cleanup del useEffect pausa el
// video). El audio es siempre el timing source; el video es complementario.
//
// Extraído de SpeakerCard.tsx en Fase 40 para reusar en CorrectionItemCard.

import { useEffect, useRef } from "react";
import styled from "styled-components";

export const SAMPLE_PADDING_BEFORE = 2;

type SampleVideoPlayerProps = {
  src: string;
  startTime: number;
};

export function SampleVideoPlayer({ src, startTime }: SampleVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    let cancelled = false;
    const seekStart = Math.max(0, startTime - SAMPLE_PADDING_BEFORE);

    const playFromStart = () => {
      if (cancelled) return;
      try {
        v.currentTime = seekStart;
      } catch {
        // seek puede fallar si el archivo está mal; ignoramos.
      }
      v.play().catch((err) => {
        console.warn("[SampleVideoPlayer] play() falló", {
          src,
          startTime,
          err,
        });
      });
    };

    if (v.readyState >= 1 && Number.isFinite(v.duration)) {
      playFromStart();
    } else {
      v.addEventListener("loadedmetadata", playFromStart, { once: true });
    }

    return () => {
      cancelled = true;
      v.removeEventListener("loadedmetadata", playFromStart);
      if (!v.paused) v.pause();
    };
  }, [src, startTime]);

  return (
    <VideoWrapper>
      <Video
        ref={videoRef}
        src={src}
        preload="metadata"
        playsInline
        onError={(e) => {
          const mediaError = e.currentTarget.error;
          console.warn("[SampleVideoPlayer] <video> error event", {
            src,
            startTime,
            code: mediaError?.code,
            message: mediaError?.message,
          });
        }}
      />
    </VideoWrapper>
  );
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const VideoWrapper = styled.div`
  border-radius: ${({ theme }) => theme.radius.sm};
  overflow: hidden;
  background: #000;
  border: 1px solid ${({ theme }) => theme.color.border};
`;

const Video = styled.video`
  display: block;
  width: 100%;
  max-height: 240px;
  object-fit: contain;
  background: #000;
`;
